import React, { useState } from "react";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import CodeBlock from "./components/CodeBlock";
import Icon from "../../components/AppIcon";

const ApiDocumentationPage = () => {
  const [activeTab, setActiveTab] = useState("overview");

  const apiData = {
    baseUrl: "https://cosmelytix.com/api",
    version: "1.0.0",
    endpoints: [
      {
        id: "list-products",
        method: "GET",
        path: "/products",
        description: "Returns a list of all products.",
        parameters: [],
        responseExample: `[
  {
    "prod_id": 120881,
    "name": "Úna",
    "brand": "Skintegra",
    "image_url": "db29e9b8d105f52-úna.webp"
  }
]`
      },
      {
        id: "get-product",
        method: "GET",
        path: "/products/{id}",
        description: "Returns full details of a specific product.",
        parameters: [
          { name: "id", type: "number", required: true, description: "Product ID" }
        ],
        responseExample: `{
  "prod_id": 120881,
  "name": "Úna",
  "brand": "Skintegra",
  "image_url": "...",
  "ingredients_short": ["Aqua", "Glycerin", "..."],
  "description": "..."
}`
      },
      {
        id: "search-products",
        method: "GET",
        path: "/products/search",
        description: "Search products by name. Returns up to 50 results.",
        parameters: [
          { name: "q", type: "string", required: true, description: "Search term (min 2 characters)" },
          { name: "limit", type: "number", required: false, description: "Max results (default: 20, max: 50)" }
        ],
        responseExample: `[
  {
    "prod_id": 432444,
    "name": "#1 Serum",
    "brand": "Rationale",
    "image_url": "644e93b47c8f7f6-1-serum.webp"
  }
]`
      },
      {
        id: "similar-products",
        method: "GET",
        path: "/products/{id}/similar",
        description: "Returns the most similar products by formulation.",
        parameters: [
          { name: "id", type: "number", required: true, description: "Product ID" },
          { name: "k", type: "number", required: false, description: "Number of results (default: 3)" }
        ],
        responseExample: `{
  "query_prod_id": 120881,
  "results": [
    { "prod_id": 456908, "name": "Night Recovery Mask", "score": 0.778 },
    { "prod_id": 591344, "name": "Atomic", "score": 0.752 },
    { "prod_id": 532521, "name": "Rejuvenating Herbal Oil-Free Gel Cream", "score": 0.744 }
  ]
}`
      },
      {
        id: "list-brands",
        method: "GET",
        path: "/brands",
        description: "Returns a list of all brands.",
        parameters: [],
        responseExample: `[
  { "brand": "Skintegra" },
  { "brand": "The Ordinary" }
]`
      },
      {
        id: "get-ingredient",
        method: "GET",
        path: "/ingredients/{name}",
        description: "Returns information about an ingredient by its slug.",
        parameters: [
          { name: "name", type: "string", required: true, description: "Ingredient slug (e.g. glycerin)" }
        ],
        responseExample: `{
  "name": "glycerin",
  "inci": "Glycerin",
  "description": "..."
}`
      },
      {
        id: "get-interactions",
        method: "GET",
        path: "/interaction",
        description: "List all active interactions of a given type, or check status for a specific product. Supported types: like, save, add.",
        parameters: [
          { name: "type", type: "string", required: true, description: "like, save, or add" },
          { name: "productId", type: "string", required: false, description: "If omitted, returns all active interactions of this type" }
        ],
        responseExample: `// All interactions (no productId)
[
  { "product_id": "120881", "interaction_type": "like", "status": 1 },
  { "product_id": "456908", "interaction_type": "like", "status": 1 }
]

// Single product check
{
  "interaction_id": "user123_120881_like",
  "product_id": "120881",
  "interaction_type": "like",
  "status": 1
}`
      },
      {
        id: "post-interaction",
        method: "POST",
        path: "/interaction",
        description: "Create an interaction. If it already exists, toggles it off. Supported types: like, save, add.",
        parameters: [
          { name: "productId", type: "string", required: true, description: "Product ID" },
          { name: "interactionType", type: "string", required: true, description: "like, save, or add" }
        ],
        responseExample: `{ "message": "Interaction of type 'like' created." }`
      }
    ],
    authExample: `const TOKEN = "<your-token>";

fetch("https://cosmelytix.com/api/products/search?q=serum", {
  headers: { Authorization: TOKEN }
})
  .then(res => res.json())
  .then(products => products.forEach(p => console.log(p.name)));`,
    languageExamples: {
      javascript: `const TOKEN = "<your-token>";

// Search products
fetch("https://cosmelytix.com/api/products/search?q=serum", {
  headers: { Authorization: TOKEN }
})
  .then(res => res.json())
  .then(products => products.forEach(p => console.log(p.name, p.brand)));`,
      python: `import requests

TOKEN = "<your-token>"

# Get similar products
resp = requests.get(
    "https://cosmelytix.com/api/products/120881/similar",
    headers={"Authorization": TOKEN},
    params={"k": 5}
)
for item in resp.json()["results"]:
    print(item["prod_id"], item["name"], item["score"])`,
      curl: `TOKEN="<your-token>"

# Search products
curl -H "Authorization: $TOKEN" \\
  "https://cosmelytix.com/api/products/search?q=serum"

# Get product
curl -H "Authorization: $TOKEN" \\
  "https://cosmelytix.com/api/products/120881"

# Find similar products
curl -H "Authorization: $TOKEN" \\
  "https://cosmelytix.com/api/products/120881/similar"

# Like a product
curl -X POST -H "Authorization: $TOKEN" -H "Content-Type: application/json" \\
  -d '{"productId":"120881","interactionType":"like"}' \\
  "https://cosmelytix.com/api/interaction"

# Get all liked products
curl -H "Authorization: $TOKEN" \\
  "https://cosmelytix.com/api/interaction?type=like"`
    }
  };

  const errorResponses = [
    {
      code: 400,
      message: "Bad Request",
      description: "Missing or invalid parameters.",
      example: `{ "message": "Missing required parameter: type" }`
    },
    {
      code: 401,
      message: "Unauthorized",
      description: "Missing or invalid JWT token.",
      example: `{ "message": "Unauthorized" }`
    },
    {
      code: 404,
      message: "Not Found",
      description: "The requested resource could not be found.",
      example: `{ "message": "Product not found" }`
    },
    {
      code: 500,
      message: "Internal Server Error",
      description: "Something went wrong on our end. Please try again later.",
      example: `{ "message": "Internal server error" }`
    }
  ];

  const methodColors = {
    GET:    { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200' },
    POST:   { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200' },
    PUT:    { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
    DELETE: { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200' },
  };

  const endpointGroups = [
    { label: 'Products',     ids: ['list-products', 'get-product', 'search-products', 'similar-products'] },
    { label: 'Brands',       ids: ['list-brands'] },
    { label: 'Ingredients',  ids: ['get-ingredient'] },
    { label: 'Interactions', ids: ['get-interactions', 'post-interaction'] },
  ];

  const navItems = [
    { id: 'overview',       label: 'Overview',       icon: 'BookOpen' },
    { id: 'authentication', label: 'Authentication',  icon: 'Key' },
    { id: 'endpoints',      label: 'Endpoints',       icon: 'Zap' },
    { id: 'errors',         label: 'Errors',          icon: 'AlertCircle' },
    { id: 'examples',       label: 'Code Examples',   icon: 'Code' },
  ];

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: '#f8f7fc' }}>
      <Header />

      {/* ── Hero ── */}
      <div style={{ background: 'linear-gradient(135deg, #1e1030 0%, #2D1735 60%, #3b1f50 100%)' }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-[#E9A969] mb-4 opacity-80">
                REST API · v{apiData.version}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                Cosmelytix<br />API Reference
              </h1>
              <p className="text-white/60 text-lg max-w-xl">
                Programmatic access to 39,000+ cosmetic products, ingredients, brands, and personalised interaction data.
              </p>
            </div>
            <div className="flex flex-col gap-3 shrink-0">
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5">
                <span className="text-white/50 text-xs font-medium uppercase tracking-wider">Base URL</span>
                <code className="text-[#E9A969] font-mono text-sm ml-2">{apiData.baseUrl}</code>
              </div>
              <a
                href="/user-account-page"
                className="text-center bg-[#E9A969] hover:bg-[#d4935a] text-[#2D1735] font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors"
              >
                Get your token →
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body: sidebar + content ── */}
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 flex flex-col lg:flex-row gap-8 flex-grow">

        {/* Sidebar */}
        <aside className="lg:w-56 xl:w-64 flex-shrink-0">
          <div className="sticky top-6 space-y-1">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                  activeTab === item.id
                    ? 'bg-[#5F40A1] text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-[#5F40A1]'
                }`}
              >
                <Icon name={item.icon} size={15} />
                {item.label}
              </button>
            ))}

            {/* Endpoint quick-nav (shown only on endpoints tab) */}
            {activeTab === 'endpoints' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                {endpointGroups.map(group => (
                  <div key={group.label} className="mb-3">
                    <p className="px-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">{group.label}</p>
                    {apiData.endpoints
                      .filter(e => group.ids.includes(e.id))
                      .map(e => (
                        <a
                          key={e.id}
                          href={`#${e.id}`}
                          className="flex items-center gap-2 px-3 py-1 rounded text-xs text-gray-500 hover:text-[#5F40A1] hover:bg-white transition-colors"
                        >
                          <span className={`font-mono font-bold text-[10px] ${methodColors[e.method]?.text ?? 'text-gray-500'}`}>
                            {e.method}
                          </span>
                          <span className="truncate">{e.path}</span>
                        </a>
                      ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 space-y-6">

          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <div>
              <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Overview</h2>
                <p className="text-gray-500 leading-relaxed mb-8">
                  The Cosmelytix API gives you programmatic access to our cosmetics database — products, ingredients, brands,
                  and user interaction data. All responses are JSON. All endpoints require a JWT token.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  {[
                    { value: '39,000+', label: 'Products' },
                    { value: '7,600+',  label: 'Brands' },
                    { value: '12,000+', label: 'Ingredients' },
                  ].map(stat => (
                    <div key={stat.label} className="rounded-lg border border-gray-100 bg-gray-50 px-5 py-4 text-center">
                      <div className="text-2xl font-bold text-[#5F40A1]">{stat.value}</div>
                      <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <h3 className="text-base font-semibold text-gray-800 mb-4">Quick start</h3>
                <ol className="space-y-4">
                  {[
                    { step: '1', title: 'Create an account', desc: <span>Sign up at <a href="/user-account-page" className="text-[#5F40A1] hover:underline">cosmelytix.com</a> to get your JWT token.</span> },
                    { step: '2', title: 'Authenticate', desc: 'Pass your token in the Authorization header on every request.' },
                    { step: '3', title: 'Start building', desc: 'Browse the endpoints below and use the code examples to integrate.' },
                  ].map(item => (
                    <li key={item.step} className="flex gap-4">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#5F40A1] text-white text-xs font-bold flex items-center justify-center">
                        {item.step}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{item.title}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Endpoint summary table */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-800">All Endpoints</h3>
                </div>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {apiData.endpoints.map(e => {
                      const mc = methodColors[e.method] ?? methodColors.GET;
                      return (
                        <tr key={e.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setActiveTab('endpoints')}>
                          <td className="px-6 py-3 w-16">
                            <span className={`inline-block text-xs font-bold font-mono px-2 py-0.5 rounded ${mc.bg} ${mc.text} border ${mc.border}`}>
                              {e.method}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-700">{e.path}</td>
                          <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{e.description}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── AUTHENTICATION ── */}
          {activeTab === 'authentication' && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication</h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                Every request must include a JWT token in the <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs text-[#5F40A1]">Authorization</code> header.
                Obtain your token by signing in at <a href="/user-account-page" className="text-[#5F40A1] hover:underline">cosmelytix.com</a>.
              </p>

              <div className="grid lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Header format</h3>
                  <div className="rounded-lg overflow-hidden border border-gray-200">
                    <div className="bg-gray-800 px-4 py-2 text-xs text-gray-400 font-mono">HTTP Header</div>
                    <div className="bg-gray-900 px-4 py-4 font-mono text-sm text-green-400">
                      Authorization: &lt;your-token&gt;
                    </div>
                  </div>

                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
                    <Icon name="Clock" size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Token lifetime: 15 minutes</p>
                      <p className="text-xs text-amber-700 mt-0.5">Re-authenticate when your token expires.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Example</h3>
                  <CodeBlock code={apiData.authExample} language="javascript" />
                </div>
              </div>
            </div>
          )}

          {/* ── ENDPOINTS ── */}
          {activeTab === 'endpoints' && (
            <div className="space-y-4">
              {endpointGroups.map(group => (
                <div key={group.label}>
                  {/* Group header */}
                  <div className="flex items-center gap-3 mb-3 mt-6 first:mt-0">
                    <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400">{group.label}</h2>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {apiData.endpoints
                    .filter(e => group.ids.includes(e.id))
                    .map(endpoint => {
                      const mc = methodColors[endpoint.method] ?? methodColors.GET;
                      return (
                        <div key={endpoint.id} id={endpoint.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
                          {/* Endpoint header */}
                          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50">
                            <span className={`text-xs font-bold font-mono px-2.5 py-1 rounded border ${mc.bg} ${mc.text} ${mc.border}`}>
                              {endpoint.method}
                            </span>
                            <code className="font-mono text-gray-800 font-medium text-sm">{endpoint.path}</code>
                            <button
                              onClick={() => navigator.clipboard.writeText(`${apiData.baseUrl}${endpoint.path}`)}
                              className="ml-auto text-gray-400 hover:text-[#5F40A1] transition-colors"
                              title="Copy full URL"
                            >
                              <Icon name="Copy" size={14} />
                            </button>
                          </div>

                          {/* Two-column body */}
                          <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
                            {/* Left: description + params */}
                            <div className="p-6">
                              <p className="text-gray-600 text-sm leading-relaxed mb-5">{endpoint.description}</p>

                              {/* Full URL */}
                              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-5">
                                <code className="font-mono text-xs text-gray-500 truncate">{apiData.baseUrl}{endpoint.path}</code>
                              </div>

                              {endpoint.parameters.length > 0 && (
                                <>
                                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Parameters</p>
                                  <div className="space-y-2">
                                    {endpoint.parameters.map((param, i) => (
                                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <code className="text-xs font-bold font-mono text-[#5F40A1]">{param.name}</code>
                                            <span className="text-[10px] font-mono text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">{param.type}</span>
                                            {param.required && (
                                              <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded">required</span>
                                            )}
                                          </div>
                                          <p className="text-xs text-gray-500 mt-1">{param.description}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Right: response */}
                            <div className="p-0 bg-gray-900">
                              <div className="px-4 py-2.5 border-b border-gray-700 flex items-center gap-2">
                                <span className="text-xs text-gray-400 font-mono">Response</span>
                                <span className="ml-auto text-[10px] bg-green-900 text-green-400 border border-green-700 px-2 py-0.5 rounded font-mono">200 OK</span>
                              </div>
                              <pre className="p-4 text-xs font-mono text-gray-300 overflow-x-auto leading-relaxed whitespace-pre-wrap">
                                {endpoint.responseExample}
                              </pre>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ))}
            </div>
          )}

          {/* ── ERRORS ── */}
          {activeTab === 'errors' && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Errors</h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                The API uses standard HTTP status codes. Errors return a JSON body with a <code className="bg-gray-100 px-1 rounded text-xs font-mono text-[#5F40A1]">message</code> field.
              </p>

              <div className="overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">Status</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-40">Name</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Response body</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {errorResponses.map(err => (
                      <tr key={err.code} className="hover:bg-gray-50">
                        <td className="px-5 py-4">
                          <span className={`font-mono font-bold text-xs px-2 py-1 rounded ${
                            err.code >= 500 ? 'bg-red-50 text-red-700 border border-red-200'
                              : err.code === 401 ? 'bg-amber-50 text-amber-700 border border-amber-200'
                              : 'bg-orange-50 text-orange-700 border border-orange-200'
                          }`}>{err.code}</span>
                        </td>
                        <td className="px-5 py-4 font-medium text-gray-700">{err.message}</td>
                        <td className="px-5 py-4 text-gray-500">{err.description}</td>
                        <td className="px-5 py-4 hidden md:table-cell">
                          <code className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{err.example}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── EXAMPLES ── */}
          {activeTab === 'examples' && (
            <div className="space-y-6">
              {[
                { title: 'cURL',       lang: 'bash',       code: apiData.languageExamples.curl },
                { title: 'JavaScript', lang: 'javascript', code: apiData.languageExamples.javascript },
                { title: 'Python',     lang: 'python',     code: apiData.languageExamples.python },
              ].map(ex => (
                <div key={ex.title} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-800">{ex.title}</h3>
                  </div>
                  <div className="p-6">
                    <CodeBlock code={ex.code} language={ex.lang} />
                  </div>
                </div>
              ))}
            </div>
          )}

        </main>
      </div>

      <Footer />
    </div>
  );
};

export default ApiDocumentationPage;